import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

// Renders a single dashboard widget chart (donut / bar / line) using Chart.js.
// Reads the data and metadata from the data-* attributes on the wrapper.
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    type:   String,  // "chart_donut" | "chart_bar" | "chart_line"
    format: String,  // "currency" | "integer"
    data:   Object   // { labels: [...], values: [...], colors: [...] }
  }

  connect() {
    if (!this.hasCanvasTarget) return
    const data = this.dataValue || {}
    const labels = data.labels || []
    const values = data.values || []
    const colors = data.colors || []

    if (!labels.length) {
      this._renderEmpty()
      return
    }

    const fmt = this._formatter(this.formatValue)
    const config = this._buildConfig(this.typeValue, labels, values, colors, fmt)
    this.chart = new Chart(this.canvasTarget, config)
  }

  disconnect() {
    this.chart?.destroy()
  }

  // -----------------------------------------------------------------

  _buildConfig(type, labels, values, colors, fmt) {
    const baseFont = { family: "'Inter', 'Poppins', sans-serif", size: 12 }
    const tooltip = {
      callbacks: {
        label: (ctx) => {
          const v = ctx.parsed?.y ?? ctx.parsed?.x ?? ctx.parsed
          return ` ${fmt(typeof v === "number" ? v : ctx.parsed)}`
        }
      }
    }

    if (type === "chart_donut") {
      return {
        type: "doughnut",
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: colors,
            borderWidth: 2,
            borderColor: "#fff",
            hoverOffset: 6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "65%",
          plugins: {
            legend: {
              position: "right",
              labels: { font: baseFont, color: "#475569", usePointStyle: true, boxWidth: 8 }
            },
            tooltip
          }
        }
      }
    }

    if (type === "chart_bar") {
      const horizontal = labels.length > 6
      return {
        type: "bar",
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: colors,
            borderRadius: 6,
            borderSkipped: false
          }]
        },
        options: {
          indexAxis: horizontal ? "y" : "x",
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { font: baseFont, color: "#475569", callback: (v) => horizontal ? fmt(v) : v },
              grid:  { color: "#f1f5f9" }
            },
            y: {
              ticks: { font: baseFont, color: "#475569", callback: (v) => horizontal ? v : fmt(v) },
              grid:  { color: "#f1f5f9" }
            }
          },
          plugins: { legend: { display: false }, tooltip }
        }
      }
    }

    if (type === "chart_line") {
      return {
        type: "line",
        data: {
          labels,
          datasets: [{
            data: values,
            borderColor: colors[0] || "#4f46e5",
            backgroundColor: this._hexToRgba(colors[0] || "#4f46e5", 0.12),
            borderWidth: 2,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ticks: { font: baseFont, color: "#475569" }, grid: { color: "#f1f5f9" } },
            y: { ticks: { font: baseFont, color: "#475569", callback: (v) => fmt(v) }, grid: { color: "#f1f5f9" }, beginAtZero: true }
          },
          plugins: { legend: { display: false }, tooltip }
        }
      }
    }
  }

  _renderEmpty() {
    const el = document.createElement("div")
    el.className = "widget__chart-empty"
    el.innerHTML = '<i class="fa-solid fa-chart-pie"></i><span>Aucune donnée</span>'
    this.element.replaceChildren(el)
  }

  _formatter(format) {
    if (format === "currency") {
      return (v) => {
        const n = Number(v) || 0
        if (Math.abs(n) >= 1_000_000) return `${(n / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} k€`
        return `${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €`
      }
    }
    return (v) => Math.round(Number(v) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
  }

  _hexToRgba(hex, a) {
    const h = hex.replace("#", "")
    const bigint = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >>  8) & 255
    const b = bigint & 255
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
}
